<!-- eslint-disable vue/no-v-html -->
<template>
  <APageHead
    title="교재 관리"
    sub="체계적인 교재 관리 시스템으로 효율적인 콘텐츠 운영을 지원합니다"
    icon="menu_book"
  >
    <template #actions>
      <ABtn kind="ghost" icon="download" @click="handleDownload">
        엑셀 다운로드
      </ABtn>
      <ABtn kind="primary" icon="add" @click="addNew">
        새 교재 등록
      </ABtn>
    </template>
  </APageHead>

  <div class="ct-page">
    <!-- 콘텐츠 탭 (교재/오디오북/비디오) -->
    <TabNav
      v-model="activeTab"
      :tabs="[
        { id: 'textbook',  label: $t('navi.04_01'), icon: 'menu_book' },
      ]"
    />

    <!-- Stats 4종 -->
    <div class="ct-stats">
      <AStat label="전체 교재"   :value="totalCount || 0"     icon="menu_book"    />
      <AStat label="저자 수"     :value="publisherCount || 0" icon="person"       />
      <AStat label="편집자 수"   :value="editorCount || 0"    icon="edit"         />
      <AStat label="선택됨"      :value="selectedItems.length" icon="check_circle"
             :accent="selectedItems.length > 0" />
    </div>

    <!-- Search -->
    <ACard :padding="16">
      <search-area
        :search-fields="searchFields"
        :show-search-button="true"
        :show-download-button="false"
        @search="handleSearch"
      />
    </ACard>

    <!-- 교재 목록 테이블 -->
    <ACard :padding="0">
      <template #head>
        <h3 class="ct-card-title">{{ $t('label.27') }}</h3>
        <p class="ct-card-sub">{{ $t('txt.09') }}</p>
      </template>
      <template #actions>
        <span class="ct-total">전체 <strong>{{ totalCount }}</strong>건</span>
      </template>

      <ATable
        :columns="columns"
        :rows="textbooks"
        row-key="barcode"
        empty-text="등록된 교재가 없습니다"
      >
        <!-- 헤더: 체크박스 (전체 선택) -->
        <template #head:select>
          <input
            type="checkbox"
            class="ct-checkbox"
            :checked="isAllSelected"
            @change="toggleAll($event.target.checked)"
          />
        </template>
        <!-- 헤더: 정렬 가능 컬럼 -->
        <template #head:title>
          <button type="button" class="ct-sort-btn" @click="toggleSort('title')">
            {{ $t('label.14') }}
            <i v-if="sortField === 'title'" class="material-icons-round ct-sort-btn__icon">
              {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
            </i>
            <i v-else class="material-icons-round ct-sort-btn__icon ct-sort-btn__icon--inactive">unfold_more</i>
          </button>
        </template>
        <template #head:publisher>
          <button type="button" class="ct-sort-btn" @click="toggleSort('publisher')">
            {{ $t('label.29') }}
            <i v-if="sortField === 'publisher'" class="material-icons-round ct-sort-btn__icon">
              {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
            </i>
            <i v-else class="material-icons-round ct-sort-btn__icon ct-sort-btn__icon--inactive">unfold_more</i>
          </button>
        </template>

        <!-- 셀: 선택 체크박스 -->
        <template #cell:select="{ row }">
          <input
            type="checkbox"
            class="ct-checkbox"
            :checked="selectedItems.includes(row.barcode)"
            @change="toggleRow(row.barcode, $event.target.checked)"
          />
        </template>

        <!-- 셀: 번호 -->
        <template #cell:num="{ index }">
          <span class="ct-num">{{ getItemNumber(index) }}</span>
        </template>

        <!-- 셀: 제목 + 표지 + 설명 (§2.8: width 360, 설명 max 280 + ellipsis) -->
        <template #cell:title="{ row }">
          <div class="ct-title">
            <img
              :src="row.cover_image_url || require('@/assets/img/no_image.png')"
              alt="cover"
              class="ct-title__cover"
              @error="onImageError"
            />
            <div class="ct-title__text">
              <div class="ct-title__name">{{ row.title }}</div>
              <div class="ct-title__desc">{{ $stripHtmlTags(row.description) }}</div>
            </div>
          </div>
        </template>

        <template #cell:publisher="{ value }">
          <span class="ct-cell-truncate">{{ value }}</span>
        </template>

        <template #cell:barcode="{ value }">
          <code class="ct-barcode">{{ value }}</code>
        </template>

        <template #cell:editor="{ row }">
          <span class="ct-cell-truncate">{{ row.editor_name }}</span>
        </template>

        <template #cell:created="{ row }">
          <span class="ct-date">{{ (row.created_at || '').substring(0, 10) }}</span>
        </template>

        <!-- 셀: 액션 (아이콘 버튼) -->
        <template #cell:actions="{ row }">
          <div class="ct-actions">
            <button
              type="button"
              class="p-icon-btn"
              title="교재 정보 편집"
              @click="goEditTextbook(row.barcode)"
            >
              <i class="material-icons-round">edit</i>
            </button>
          </div>
        </template>
      </ATable>
    </ACard>

    <!-- Pagination footer (항상 표시; 페이지 버튼은 2페이지 이상일 때만) -->
    <div class="ct-foot">
      <div class="ct-foot__info">
        총 <strong>{{ totalCount }}</strong>건 ·
        <span class="ct-foot__page">{{ currentPage }} / {{ Math.max(totalPages, 1) }} 페이지</span>
      </div>
      <Pagination
        v-if="totalPages > 1"
        v-model:current-page="currentPage"
        :total-pages="totalPages"
        @update:current-page="getTextbooks"
      />
    </div>
  </div>
</template>

<script>
import TabNav from '@/components/TabNav.vue';
import Pagination from '@/components/Pagination.vue';
import SearchArea from '@/components/SearchArea.vue';
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AStat from '@/components/Pineapple/AStat.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import * as XLSX from 'xlsx';

export default {
  name: 'ContentsTextbooks',
  components: {
    TabNav,
    Pagination,
    SearchArea,
    APageHead,
    ACard,
    AStat,
    ATable,
    ABtn,
  },
  data() {
    return {
      textbooks: [],
      activeTab: 'textbook',
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 10,
      totalCount: 0,
      searchValues: {},
      selectedItems: [],
      sortField: '',
      sortOrder: 'asc',
      searchFields: [
        { key: 'title',     placeholder: this.$t('label.14') },
        { key: 'publisher', placeholder: this.$t('label.29') },
        { key: 'barcode',   placeholder: this.$t('label.28') },
      ],
      columns: [
        { key: 'select',   label: '',           align: 'center', width: 50 },
        { key: 'num',      label: '#',          align: 'center', width: 60 },
        { key: 'title',    label: '제목',        align: 'left',   width: 360 },
        { key: 'publisher',label: '저자',        align: 'left',   width: 160 },
        { key: 'barcode',  label: '바코드',      align: 'left',   width: 140 },
        { key: 'editor',   label: '편집자',      align: 'left',   width: 120 },
        { key: 'created',  label: '생성일',      align: 'left',   width: 110 },
        { key: 'actions',  label: '',           align: 'right',  width: 130 },
      ],
    };
  },
  computed: {
    userInfo() {
      return this.$store.state.auth.user_info || {};
    },
    getItemNumber() {
      return (index) => this.totalCount - ((this.currentPage - 1) * this.itemsPerPage + index);
    },
    publisherCount() {
      const uniquePublishers = new Set(this.textbooks.map(item => item.publisher).filter(Boolean));
      return uniquePublishers.size;
    },
    editorCount() {
      const uniqueEditors = new Set(this.textbooks.map(item => item.editor_name).filter(Boolean));
      return uniqueEditors.size;
    },
    isAllSelected() {
      return this.textbooks.length > 0 && this.selectedItems.length === this.textbooks.length;
    },
  },
  watch: {
    activeTab(newValue) {
      const routes = {
        textbook: 'ContentsTextbooks',
        audiobook: 'ContentsAudiobooks',
        video: 'ContentsVideos',
      };
      if (this.$route.name !== routes[newValue]) {
        this.$router.push({ name: routes[newValue] });
      }
    },
  },
  mounted() {
    const queryPage = parseInt(this.$route.query.page);
    if (queryPage && queryPage > 0) {
      this.currentPage = queryPage;
    }
    if (this.userInfo && this.userInfo.name) {
      this.getTextbooks();
    }
  },
  methods: {
    async getTextbooks() {
      try {
        const params = {
          editor_idx: this.userInfo.idx,
          limit: this.itemsPerPage,
          page: this.currentPage,
          search_params: JSON.stringify(this.searchValues),
        };
        if (this.sortField) {
          params.sort_field = this.sortField;
          params.sort_order = this.sortOrder;
        }
        const res = await this.$axios({
          method: 'get',
          url: '/textbook/editor/textbooks',
          params,
          loadingBar: true,
        });
        if (res.status === 200) {
          this.textbooks = res.data.items;
          const total = res.data.total_count;
          this.totalCount = total;
          this.totalPages = Math.ceil(total / this.itemsPerPage);
          this.selectedItems = [];
        }
      } catch (error) {
        console.error('Error :', error);
      }
    },
    addNew() {
      this.$router.push({ name: 'EditTextbook' });
    },
    goEditTextbook(barcode) {
      this.$router.push({ name: 'EditTextbook', query: { barcode, page: this.currentPage } });
    },
    handleSearch(searchValues) {
      this.searchValues = searchValues;
      this.currentPage = 1;
      this.getTextbooks();
    },
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortOrder = 'asc';
      }
      this.currentPage = 1;
      this.getTextbooks();
    },
    toggleAll(isChecked) {
      this.selectedItems = isChecked ? this.textbooks.map(item => item.barcode) : [];
    },
    toggleRow(barcode, checked) {
      const idx = this.selectedItems.indexOf(barcode);
      if (checked && idx === -1) this.selectedItems.push(barcode);
      else if (!checked && idx !== -1) this.selectedItems.splice(idx, 1);
    },
    onImageError(e) {
      try {
        e.target.src = require('@/assets/img/no_image.png');
      } catch (err) { /* ignore */ }
    },
    async handleDownload() {
      try {
        const params = {
          editor_idx: this.userInfo.idx,
          limit: 9999,
          page: 1,
          search_params: JSON.stringify(this.searchValues),
        };
        if (this.sortField) {
          params.sort_field = this.sortField;
          params.sort_order = this.sortOrder;
        }
        const res = await this.$axios({
          method: 'get',
          url: '/textbook/editor/textbooks',
          params,
          loadingBar: true,
        });
        if (res.status === 200) {
          const allTextbooks = res.data.items;
          const excelData = allTextbooks.map((item, index) => ({
            '번호': allTextbooks.length - index,
            '제목': item.title,
            '저자': item.publisher,
            '설명': this.$stripHtmlTags(item.description),
            '생성일': item.created_at,
          }));
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(excelData);
          ws['!cols'] = [{ wch: 10 }, { wch: 30 }, { wch: 20 }, { wch: 50 }, { wch: 20 }];
          XLSX.utils.book_append_sheet(wb, ws, '교재 목록');
          const fileName = `교재_목록_${new Date().toISOString().split('T')[0]}.xlsx`;
          XLSX.writeFile(wb, fileName);
        }
      } catch (error) {
        console.error('Error downloading excel:', error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ct-page {
  padding: 20px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: $p-font-body;
}

.ct-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

// ACard head/actions styling
.ct-card-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: $p-ink;
}

.ct-card-sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: $p-ink-mid;
}

.ct-total {
  font-size: 12px;
  color: $p-ink-mid;
  strong {
    font-family: $p-font-mono;
    color: $p-ink;
    font-weight: 800;
  }
}

// Sortable column header
.ct-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
  letter-spacing: inherit;
  text-transform: inherit;

  &:hover { color: $p-ink; }
}

.ct-sort-btn__icon {
  font-size: 14px !important;
  color: $p-point-deep;

  &--inactive {
    color: $p-ink-soft;
    opacity: 0.6;
  }
}

// Checkbox
.ct-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: $p-point-deep;
}

// Cell number
.ct-num {
  font-family: $p-font-mono;
  font-size: 11px;
  font-weight: 800;
  color: $p-ink-mid;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  padding: 2px 8px;
  border-radius: 5px;
}

// Title cell — Textbook-Log §2.8 (width 360 from column def, desc max 280 + ellipsis)
.ct-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ct-title__cover {
  width: 40px;
  height: 52px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid $p-line;
  background: $p-panel-alt;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(31, 27, 20, 0.08);
}

.ct-title__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.ct-title__name {
  font-size: 13px;
  font-weight: 700;
  color: $p-ink;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ct-title__desc {
  font-size: 11px;
  color: $p-ink-mid;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.ct-cell-truncate {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
  font-size: 13px;
  color: $p-ink2;
}

.ct-barcode {
  font-family: $p-font-mono;
  font-size: 11px;
  font-weight: 700;
  color: $p-ink2;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  padding: 2px 8px;
  border-radius: 5px;
}

.ct-date {
  font-family: $p-font-mono;
  font-size: 12px;
  color: $p-ink-soft;
}

.ct-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
}

.ct-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 4px;
  flex-wrap: wrap;
}

.ct-foot__info {
  font-size: 12px;
  color: $p-ink-mid;

  strong {
    font-family: $p-font-mono;
    color: $p-ink;
    font-weight: 800;
  }
}

.ct-foot__page {
  font-family: $p-font-mono;
  color: $p-ink2;
  font-weight: 700;
  margin-left: 4px;
}

@media (max-width: 1100px) {
  .ct-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 640px) {
  .ct-stats { grid-template-columns: 1fr; }
}
</style>
